import { useMemo } from 'react';
import { Box, Button, CircularProgress, Collapse, Dialog, DialogContent, IconButton } from '@mui/material';
import type { SxProps, Theme } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import { useWatch } from 'react-hook-form';
import type { GridSelectionModel } from '@mui/x-data-grid';
import { colors } from 'Constants/Colors';
import { PRIORITY_ID } from 'Constants/priority';
import useTranslation from 'Hooks/useTranslation';
import useEligibleEmployeesForApproval from '../../Hooks/useEligibleEmployeesForApproval';
import EvidenceSection from './EvidenceSection';
import EmployeeSelectionPanel from './EmployeeSelectionPanel';
import useBulkApproveForm from './useBulkApproveForm';
import { styles } from './styles';
import type { UserDevelopmentPlanI } from 'types';

interface ApproveParams {
  userCourseIds: string[];
  file: File | null;
  evaluationDate?: Date;
  courseId: string;
  instructorId: string | null;
  instructorUserId: string | null;
}

interface Props {
  open: boolean;
  course: UserDevelopmentPlanI | null;
  onClose: () => void;
  onApprove: (params: ApproveParams) => Promise<boolean>;
}

const closeButtonSx: SxProps<Theme> = {
  color: colors.GREEN,
  borderColor: colors.GREEN,
  fontWeight: 'bold',
  textTransform: 'none',
  '&:hover': { borderColor: colors.GREEN_LEAF, bgcolor: colors.WHITE },
};

const approveButtonSx = (enabled: boolean): SxProps<Theme> => ({
  bgcolor: enabled ? colors.GREEN : colors.GRAY,
  color: colors.WHITE,
  fontWeight: 'bold',
  textTransform: 'none',
  '&:hover': { bgcolor: enabled ? colors.GREEN_LEAF : colors.GRAY },
  '&.Mui-disabled': { bgcolor: colors.GRAY, color: colors.WHITE },
});

const footerSx: SxProps<Theme> = {
  borderTop: `1px solid ${colors.GRAY_HR}`,
  pt: 2,
  mt: 1,
  display: 'flex',
  gap: 1,
  justifyContent: 'flex-end',
};

const BulkApproveModal = ({ open, course, onClose, onApprove }: Props) => {
  const { t2 } = useTranslation('EmployeeAssess');

  const courseId = course?.Course?.id ?? null;
  const priorityId = course?.Course?.Priority?.id ?? '';
  const currentUserCourseId = course?.userCourseId ?? null;
  const isNormativa = priorityId === PRIORITY_ID.normativa;
  const isTecnica = priorityId === PRIORITY_ID.tecnica;

  const {
    form, expanded, setExpanded,
    page, setPage, pageSize, setPageSize,
    search, setSearch, debouncedSearch,
  } = useBulkApproveForm({ open, priorityId, currentUserCourseId });

  const { rows, totalEmployees, loading } = useEligibleEmployeesForApproval({
    courseId,
    enabled: open,
    page,
    pageSize,
    fullName: debouncedSearch || undefined,
  });

  const selectedIds = useWatch({ control: form.control, name: 'selectedUserCourseIds' }) ?? [];
  const watchedFile = useWatch({ control: form.control, name: 'file' });
  const selectedCount = selectedIds.length;
  const otherEligibleCount = Math.max(0, totalEmployees - 1);

  const visibleRowIds = useMemo(() => new Set(rows.map(r => r.id)), [rows]);
  const visibleSelection: GridSelectionModel = useMemo(
    () => selectedIds.filter(id => visibleRowIds.has(id)),
    [selectedIds, visibleRowIds]
  );

  const handleSelectionChange = (model: GridSelectionModel) => {
    const visibleSet = visibleRowIds;
    const persistentMinusVisible = selectedIds.filter(id => !visibleSet.has(id));
    const merged = new Set<string>([...persistentMinusVisible, ...model.map(String)]);
    if (currentUserCourseId) merged.add(currentUserCourseId);
    form.setValue('selectedUserCourseIds', Array.from(merged), { shouldValidate: false });
  };

  const handleSubmit = form.handleSubmit(async values => {
    if (!courseId) return;
    const ok = await onApprove({
      userCourseIds: values.selectedUserCourseIds,
      file: values.file,
      evaluationDate: values.evaluationDateTouched ? values.evaluationDate : undefined,
      courseId,
      // Manda solo la fuente elegida; el backend nunca debe recibir ambas.
      instructorId: values.instructorType === 'external' ? values.instructorId ?? null : null,
      instructorUserId: values.instructorType === 'internal' ? values.instructorUserId ?? null : null,
    });
    if (ok) onClose();
  });

  const handleClose = () => {
    if (form.formState.isSubmitting) return;
    onClose();
  };

  if (!course) return null;

  const isSubmitting = form.formState.isSubmitting;
  const submitDisabled = isSubmitting || (isTecnica && !watchedFile);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown={isSubmitting}
    >
      <DialogContent sx={{ pt: 3, position: 'relative' }}>
        <IconButton
          onClick={handleClose}
          disabled={isSubmitting}
          sx={{ position: 'absolute', top: 8, right: 8, color: colors.GRAY_DARK, zIndex: 1 }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={styles.layout}>
          <EvidenceSection
            control={form.control}
            setValue={form.setValue}
            priorityId={priorityId}
            expanded={expanded}
            toggleExpanded={() => setExpanded(prev => !prev)}
            eligibleCount={otherEligibleCount}
          />
          <Collapse in={expanded} timeout={320} unmountOnExit>
            <EmployeeSelectionPanel
              rows={rows}
              totalEmployees={totalEmployees}
              loading={loading}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              search={search}
              onSearchChange={setSearch}
              selectionModel={visibleSelection}
              onSelectionChange={handleSelectionChange}
              lockedRowId={currentUserCourseId}
            />
          </Collapse>

          <Box sx={footerSx}>
            <Button variant="outlined" onClick={handleClose} disabled={isSubmitting} sx={closeButtonSx}>
              {t2('Close')}
            </Button>
            {!isNormativa && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={submitDisabled}
                startIcon={isSubmitting ? <CircularProgress size={16} sx={{ color: colors.WHITE }} /> : null}
                sx={approveButtonSx(!submitDisabled)}
              >
                {selectedCount > 1
                  ? t2('approve_button_count').replace('{n}', String(selectedCount))
                  : t2('Approve')}
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BulkApproveModal;
